import { motion, Variants } from 'motion/react'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useState,
  useRef,
  useContext,
} from 'react'
import { LocomotiveScrollProvider } from 'react-locomotive-scroll'
import { useIntersectionObserver } from 'usehooks-ts'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanityAnyPage } from '@data/sanity/queries/types/page'
import { pageTransitionSpeed } from '@lib/animate'
import { isBrowser, isMobileSafari } from '@lib/helpers'
import { LayoutContext } from '@lib/layout-context'
import { SiteContext } from '@lib/site-context'
import CookieBar from '@modules/shared/cookie-bar'
import Footer from '@modules/shared/footer'
import Header, { HeaderSizeValues } from '@modules/shared/header'
import VideoModal from '@components/video/video-modal'
import HeadSeo from './head-seo'

interface CSSPropertiesWithHeader extends CSSProperties {
  '--headerHeight'?: string
}

interface LayoutProps {
  site: SanitySiteFragment
  page: SanityAnyPage
  children: ReactNode
  canonicalUrl?: string
  blogPostSchema?: string
  hasSmoothScroll?: boolean
  draftMode?: boolean
  className?: string
}

const variants: Variants = {
  initial: {
    opacity: 0,
  },
  enter: {
    opacity: 1,
    transition: {
      duration: pageTransitionSpeed / 1000,
      delay: 0.2,
      ease: 'linear',
      when: 'beforeChildren',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: pageTransitionSpeed / 1000,
      ease: 'linear',
      when: 'beforeChildren',
    },
  },
}

const DraftModeOverlay = () => (
  <div className="sticky bottom-0 inset-x-0 z-90 bg-white bg-opacity-95">
    <div className="flex justify-between items-center container py-1">
      <p className="m-0 text-sm">Preview mode is active.</p>
      <NextLink href="/api/disable-draft" prefetch={false}>
        <button className="underline decoration-1 hover:decoration-2 text-current text-sm">
          Exit preview mode
        </button>
      </NextLink>
    </div>
  </div>
)

const Layout = ({
  site,
  page,
  canonicalUrl,
  blogPostSchema,
  hasSmoothScroll,
  draftMode,
  children,
  className,
}: LayoutProps) => {
  const { windowSize } = useContext(LayoutContext)
  const { setHeaderHeight } = useContext(SiteContext)

  const scrollContainerRef = useRef(null)

  const { route } = useRouter()

  // Save relevant header sizes
  const [headerSizeStyle, setHeaderSizeStyle] =
    useState<CSSPropertiesWithHeader>({})

  // Set window height lock (with Safari/iOS hack)
  const [lockHeight, setLockHeight] = useState(false)
  const hasChin = isMobileSafari()

  useEffect(() => {
    if (isBrowser && !lockHeight) {
      document.body.style.setProperty(
        '--vh',
        `${(windowSize.height ?? 0) * 0.01}px`,
      )
      setLockHeight(hasChin)
    }
  }, [hasChin, lockHeight, windowSize])

  const handleHeaderResize = useCallback(
    ({ height }: HeaderSizeValues) => {
      setHeaderHeight(height)

      setHeaderSizeStyle({
        '--headerHeight': `${height}px`,
      })
    },
    [setHeaderHeight],
  )

  const { ref: topObserverRef, isIntersecting } = useIntersectionObserver({
    threshold: 1,
  })

  let pageContent = (
    <div style={headerSizeStyle} className={className}>
      <Header
        menuDesktopLeft={site.header?.menuDesktopLeft}
        menuDesktopRight={site.header?.menuDesktopRight}
        menuMobilePrimary={site.header?.menuMobilePrimary}
        menuMobileSecondary={site.header?.menuMobileSecondary}
        promo={site.header?.promo}
        logo={site.identity?.logo}
        invertedLogo={site.identity?.invertedLogo}
        onResize={handleHeaderResize}
        isInView={isIntersecting}
      />

      <div
        className="min-h-screen flex flex-col justify-between overflow-x-hidden"
        data-scroll-container={hasSmoothScroll}
        ref={scrollContainerRef}
      >
        <main id="content" data-scroll-content={hasSmoothScroll}>
          <span
            ref={topObserverRef}
            className="relative top-[var(--headerHeight, 0px)]"
          />

          {children}
        </main>

        {!!site.footer && (
          <Footer
            blocks={site.footer.blocks}
            copyright={site.footer.copyright}
          />
        )}
      </div>
      <VideoModal />
    </div>
  )

  if (site.settings?.pageAnimation) {
    pageContent = (
      <motion.div
        initial="initial"
        animate="enter"
        exit="exit"
        variants={variants}
      >
        {pageContent}
      </motion.div>
    )
  }

  if (hasSmoothScroll) {
    pageContent = (
      <LocomotiveScrollProvider
        options={{ smooth: true }}
        watch={[route]}
        containerRef={scrollContainerRef}
      >
        {pageContent}
      </LocomotiveScrollProvider>
    )
  }

  return (
    <>
      <HeadSeo
        page={page}
        canonicalUrl={canonicalUrl}
        blogPostSchema={blogPostSchema}
      />

      {site.cookieConsent && (
        <CookieBar
          enabled={site.cookieConsent.enabled}
          message={site.cookieConsent.message}
          link={site.cookieConsent.link}
        />
      )}

      {pageContent}

      {!!draftMode && <DraftModeOverlay />}
    </>
  )
}

export default Layout
